import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, Loader2, RefreshCw, RotateCcw, Send, ShieldAlert, Users
} from 'lucide-react';
import { Button } from '../ui/button';
import { Toast } from '../ui/Toast';
import { LongToast, shouldUseLongToast } from '../ui/LongToast';
import ToastPortal from '../ToastPortal';
import { api } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';

const formatMontant = (montant) => (
  `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(montant || 0)))} FCFA`
);

const BADGE = 'inline-flex items-center justify-center w-[132px] h-6 rounded-full text-xs font-medium whitespace-nowrap';

const RAISONS = {
  site_injoignable: 'Site injoignable au moment de la propagation',
  accord_financier_actif: 'Accord financier en cours sur le site distant'
};

/**
 * Ce que le Hub croit, ce que les sites appliquent, et ce qui reste à appliquer.
 *
 * L'écran existe parce qu'une propagation ratée ne laissait qu'un message dans
 * les logs du serveur : l'école de référence validait sa réduction, l'autre ne
 * la recevait jamais, et rien dans l'application ne permettait de s'en douter.
 */
export default function CoherenceInterSitePanel() {
  const [coherence, setCoherence] = useState(null);
  const [attentes, setAttentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreurChargement, setErreurChargement] = useState('');
  const [erreurFile, setErreurFile] = useState('');
  const [repriseEnCours, setRepriseEnCours] = useState(null);
  const [toastInfo, setToastInfo] = useState(null);

  const hideToast = () => setToastInfo(null);

  const charger = useCallback(async () => {
    setLoading(true);
    setErreurChargement('');
    setErreurFile('');
    try {
      // Les deux lectures sont indépendantes : si la file n'est pas disponible
      // — migration non passée, par exemple — la comparaison des sites reste
      // consultable. Une moitié d'information vaut mieux qu'un écran vide.
      const [rapport, file] = await Promise.all([
        api.familles.verifierCoherenceInterSite(),
        api.familles.getPropagationsInterSiteEnAttente().catch((error) => ({ success: false, error: error.message }))
      ]);

      if (rapport?.success === false) throw new Error(rapport.error || 'Vérification impossible');
      setCoherence(rapport);

      if (file?.success === false) {
        setErreurFile(file.error || 'La file des reprises est indisponible.');
        setAttentes([]);
      } else {
        setAttentes(file?.propagations || []);
      }
    } catch (error) {
      // Un échec de chargement laisse l'écran vide : un toast disparaîtrait en
      // laissant un blanc inexpliqué, le bandeau reste.
      setErreurChargement(error.message || 'Impossible de charger l\'état de cohérence.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const rejouer = async (propagation) => {
    setRepriseEnCours(propagation.id);
    try {
      const resultat = await api.familles.rejouerPropagationInterSite(propagation.id);
      if (resultat?.success === false) throw new Error(resultat.error || 'Reprise impossible');

      setToastInfo({
        type: resultat.statut === 'complet' ? 'success' : 'info',
        title: resultat.statut === 'complet' ? 'Propagation reprise' : 'Reprise partielle',
        message: resultat.resume || 'Reprise effectuée.',
        long: resultat.statut !== 'complet'
      });
      await charger();
    } catch (error) {
      setToastInfo({ type: 'error', title: 'Reprise impossible', message: error.message });
    } finally {
      setRepriseEnCours(null);
    }
  };

  // Un accord financier ne se recompose pas depuis un autre site : la valeur du
  // groupe devient une demande au Centre de Support de l'école concernée, qui
  // dispose déjà de la mécanique de recomposition.
  const demander = async (propagation) => {
    setRepriseEnCours(propagation.id);
    try {
      const resultat = await api.familles.demanderApplicationPropagationInterSite(propagation.id);
      if (resultat?.success === false) throw new Error(resultat.error || 'Demande impossible');

      setToastInfo({
        type: 'success',
        title: 'Demande envoyée',
        message: resultat.message || 'La demande a été envoyée au Centre de Support.',
        long: true
      });
      await charger();
    } catch (error) {
      setToastInfo({ type: 'error', title: 'Demande impossible', message: error.message });
    } finally {
      setRepriseEnCours(null);
    }
  };

  const ecarts = useMemo(() => (
    (coherence?.familles || []).filter((famille) => famille.nb_ecarts > 0)
  ), [coherence]);

  const valeursMortes = useMemo(() => {
    const lignes = [];
    for (const famille of coherence?.familles || []) {
      for (const site of famille.sites || []) {
        if (site.valeur_sans_beneficiaire) lignes.push({ famille, site });
      }
    }
    return lignes;
  }, [coherence]);

  if (loading) {
    return <LoadingSpinner message="Lecture de l'état des sites…" />;
  }

  if (erreurChargement) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">L'état de cohérence n'a pas pu être lu.</p>
            <p className="mt-1">{erreurChargement}</p>
            <Button variant="outline" className="h-9 mt-3 gap-2" onClick={charger}>
              <RefreshCw className="w-4 h-4" />
              Réessayer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          Comparaison entre la valeur portée par le groupe et celle appliquée par chaque site,
          et liste de ce qui n'a pas encore pu être transmis.
        </p>
        <Button variant="outline" className="h-9 gap-2" onClick={charger}>
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </Button>
      </div>

      {/* ── Reprises en attente ────────────────────────────────────────────── */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Propagations en attente</h3>
          <span className="text-xs text-gray-500">{attentes.length} en souffrance</span>
        </div>

        {erreurFile ? (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">La file des reprises n'est pas disponible.</p>
              <p className="mt-0.5">{erreurFile}</p>
            </div>
          </div>
        ) : attentes.length === 0 ? (
          <div className="mt-3 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            Toutes les réductions décidées ont été transmises aux sites concernés.
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {attentes.map((attente) => (
              <div key={attente.id} className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-amber-900">
                      {attente.famille_nom || 'Famille'} → {attente.site_subdomain}
                    </div>
                    <p className="mt-0.5 text-xs text-amber-800">
                      {RAISONS[attente.raison] || attente.raison}
                      {attente.tentatives > 1 ? ` · ${attente.tentatives} tentatives` : ''}
                    </p>
                    <p className="mt-1 text-xs text-amber-900">
                      À appliquer : {formatMontant(attente.reduction_mensuel)}/mois
                      {Number(attente.reduction_inscription) > 0
                        ? ` · ${formatMontant(attente.reduction_inscription)} sur les frais de début d'année`
                        : ''}
                    </p>
                    {attente.derniere_erreur && (
                      <p className="mt-1 text-[11px] text-amber-700">{attente.derniere_erreur}</p>
                    )}
                    {Array.isArray(attente.detail?.eleves) && attente.detail.eleves.length > 0 && (
                      <p className="mt-1 text-[11px] text-amber-700">
                        Élèves concernés : {attente.detail.eleves.map((e) => e.eleve).filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                  {/* Deux gestes différents, pas deux libellés du même. Un site
                      injoignable se rejoue tel quel ; un accord financier ne se
                      recompose que par une décision de l'école concernée. */}
                  <Button
                    variant="outline"
                    className="h-8 px-3 text-xs gap-2 flex-shrink-0"
                    disabled={repriseEnCours === attente.id}
                    onClick={() => (attente.raison === 'accord_financier_actif'
                      ? demander(attente)
                      : rejouer(attente))}
                  >
                    {repriseEnCours === attente.id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : attente.raison === 'accord_financier_actif'
                        ? <Send className="w-4 h-4" />
                        : <RotateCcw className="w-4 h-4" />}
                    {repriseEnCours === attente.id
                      ? 'En cours…'
                      : attente.raison === 'accord_financier_actif'
                        ? 'Créer la demande'
                        : 'Reprendre'}
                  </Button>
                </div>
              </div>
            ))}
            <p className="text-[11px] text-gray-500">
              La reprise applique la valeur actuelle du site de référence, pas celle figée au moment
              de l'échec : entre-temps, cette école a pu changer d'avis.
            </p>
          </div>
        )}
      </div>

      {/* ── Écarts Hub / sites ─────────────────────────────────────────────── */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Écarts entre le groupe et les sites</h3>
          <span className="text-xs text-gray-500">
            {coherence?.nb_familles || 0} famille(s) · {coherence?.nb_ecarts || 0} écart(s)
          </span>
        </div>

        {/* Les familles alignées se montrent aussi. N'afficher que les écarts
            revenait à laisser l'écran vide quand tout va bien : impossible d'y
            confirmer qu'une propagation a bien atterri sur l'autre site, ce qui
            est pourtant la première chose qu'on vient y vérifier. */}
        {(coherence?.familles || []).length === 0 ? (
          <div className="mt-3 rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-xs text-gray-500">
            Aucune famille inter-sites active dans ce groupe.
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {(coherence?.familles || []).filter((f) => f.nb_ecarts === 0).map((famille) => (
              <div
                key={`aligne-${famille.famille_uuid}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2"
              >
                <div className="min-w-0 text-xs text-emerald-900">
                  <span className="font-semibold">{famille.famille_nom}</span>
                  <span className="text-emerald-800">
                    {' '}— référence {famille.site_reference} ·{' '}
                    {formatMontant(famille.reduction_mensuel_hub)}/mois ·{' '}
                    {(famille.sites || []).length} site(s)
                  </span>
                </div>
                <span className={`${BADGE} bg-emerald-100 text-emerald-800`}>Aligné</span>
              </div>
            ))}
          </div>
        )}

        {ecarts.length > 0 && (
          <div className="mt-3 space-y-3">
            {ecarts.map((famille) => (
              <div key={famille.famille_uuid} className="rounded-md border border-gray-200 p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900">{famille.famille_nom}</span>
                  <span className="text-xs text-gray-500">
                    Référence : {famille.site_reference} · groupe : {formatMontant(famille.reduction_mensuel_hub)}/mois
                  </span>
                </div>

                <div className="mt-2 space-y-2">
                  {(famille.sites || []).filter((site) => site.etat !== 'aligne').map((site) => (
                    <div
                      key={`${famille.famille_uuid}-${site.site_subdomain}`}
                      className="flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2"
                    >
                      <div className="min-w-0 text-xs text-amber-900">
                        <span className="font-semibold">{site.site_subdomain}</span>
                        {site.etat === 'site_injoignable' && ' — site injoignable'}
                        {site.etat === 'famille_locale_introuvable' && ' — famille locale introuvable'}
                        {site.etat === 'ecart' && (
                          <>
                            {' — applique '}
                            {formatMontant(site.reduction_mensuel_locale)}/mois
                            {site.ecart_inscription
                              ? ` · ${formatMontant(site.reduction_inscription_locale)} sur les frais de début d'année`
                              : ''}
                            {site.uuid_desynchronise ? ' · lien au groupe absent' : ''}
                          </>
                        )}
                      </div>
                      <span className={`${BADGE} bg-amber-100 text-amber-800`}>
                        {site.etat === 'ecart' ? 'Valeur divergente' : 'Non vérifiable'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Valeurs que personne ne suit ───────────────────────────────────── */}
      {valeursMortes.length > 0 && (
        <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 mt-0.5 text-[#246990] flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-[#246990]">Valeurs enregistrées sans bénéficiaire</h3>
              <p className="mt-1 text-xs text-gray-600">
                Une réduction famille portée par un site où aucun élève ne la suit ne réduit rien.
                Elle se propage pourtant, et sera reprise par le prochain élève rattaché.
              </p>
              <div className="mt-2 space-y-1">
                {valeursMortes.map(({ famille, site }) => (
                  <div
                    key={`morte-${famille.famille_uuid}-${site.site_subdomain}`}
                    className="flex flex-wrap items-center gap-2 text-xs text-gray-700"
                  >
                    <Users className="w-3.5 h-3.5 text-gray-400" />
                    <span className="font-semibold">{famille.famille_nom}</span>
                    <span className="text-gray-500">sur {site.site_subdomain} :</span>
                    <span>{formatMontant(site.reduction_mensuel_locale)}/mois pour {site.nb_eleves} élève(s), aucun ne la suit</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {toastInfo && (
        <ToastPortal>
          {shouldUseLongToast(toastInfo.message, toastInfo.type, toastInfo) ? (
            <LongToast show title={toastInfo.title} message={toastInfo.message} type={toastInfo.type} onClose={hideToast} />
          ) : (
            <Toast message={toastInfo.message} type={toastInfo.type} onClose={hideToast} />
          )}
        </ToastPortal>
      )}
    </div>
  );
}
